import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import {
  buildContractPartyOptions,
  ContractPartyOption,
  PartyMode,
  resolvePreferredRepresentative
} from '../../../contracts/party-resolution/contract-party-resolution';
import { Company } from '../../../company/company.model';
import { CompanyService } from '../../../company/company.service';
import { Customer } from '../../../customer/customer.model';
import { CustomerService } from '../../../customer/customer.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { CrmService } from '../../crm.service';
import { CrmQuotationStatus } from '../../crm.model';
import { QuoteToContractMappingItem, QuoteToContractPreview, QuoteToContractRequest, QuotationContractLink } from '../quotation-contract-conversion.model';

@Component({
  selector: 'app-quotation-contract-conversion',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './quotation-contract-conversion.component.html',
  styleUrls: ['./quotation-contract-conversion.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationContractConversionComponent implements OnInit {
  private readonly tabKeys = ['parties', 'terms', 'lines', 'rates', 'obligations', 'documents', 'warnings', 'audit'] as const;
  quotationId?: number;
  preview = signal<QuoteToContractPreview | null>(null);
  items = signal<QuoteToContractMappingItem[]>([]);
  links = signal<QuotationContractLink[]>([]);
  loading = signal(false);
  converting = signal(false);
  activeTab = signal('parties');

  firstPartyReps: Customer[] = [];
  secondPartyReps: Customer[] = [];
  firstPartyMode: PartyMode | null = 'SELF';
  secondPartyMode: PartyMode | null = null;
  firstPartyCrmOptions: ContractPartyOption[] = [];
  secondPartyCrmOptions: ContractPartyOption[] = [];
  selectedFirstPartyCrmOption: ContractPartyOption | null = null;
  selectedSecondPartyCrmOption: ContractPartyOption | null = null;
  quotationBuyerNeedsManualSelection = false;

  request: QuoteToContractRequest = {
    mode: 'NEW_CONTRACT',
    effectiveStartDate: new Date(),
    selectedItems: []
  };

  tabs = [
    { id: 'parties', label: 'Parties', icon: 'fa-solid fa-building-user' },
    { id: 'terms', label: 'Terms', icon: 'fa-solid fa-file-lines' },
    { id: 'lines', label: 'Lines', icon: 'fa-solid fa-list' },
    { id: 'rates', label: 'Rates', icon: 'fa-solid fa-route' },
    { id: 'obligations', label: 'Obligations', icon: 'fa-solid fa-clipboard-check' },
    { id: 'documents', label: 'Documents', icon: 'fa-solid fa-folder-open' },
    { id: 'warnings', label: 'Warnings', icon: 'fa-solid fa-triangle-exclamation' },
    { id: 'audit', label: 'Audit', icon: 'fa-solid fa-clock-rotate-left' }
  ];

  modeOptions = [
    { label: 'New Contract', value: 'NEW_CONTRACT' },
    { label: 'Amendment', value: 'AMENDMENT' }
  ];

  firstPartyModeOptions = [
    { label: 'My company', value: 'SELF' as PartyMode },
    { label: 'CRM supplier', value: 'CRM' as PartyMode }
  ];

  secondPartyModeOptions = [
    { label: 'My company', value: 'SELF' as PartyMode },
    { label: 'CRM client', value: 'CRM' as PartyMode }
  ];

  targetOptions = [
    { label: 'Quote Only', value: 'QUOTE_ONLY' },
    { label: 'Reference Only', value: 'REFERENCE_ONLY' },
    { label: 'Contract Term', value: 'CONTRACT_TERM' },
    { label: 'Rate Card', value: 'RATE_CARD' },
    { label: 'Obligation', value: 'OBLIGATION' },
    { label: 'Document', value: 'DOCUMENT' }
  ];

  private partyOptionsLoaded = false;
  private previewLoaded = false;
  private buyerDefaultResolved = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private crmService: CrmService,
    private companyService: CompanyService,
    private customerService: CustomerService,
    private sessionStorageService: SessionStorageService,
    private toolbarService: ToolbarService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!Number.isFinite(id)) {
      return;
    }
    this.quotationId = id;
    this.request.mode = this.route.snapshot.queryParamMap.get('mode') || this.request.mode;
    const targetContractId = Number(this.route.snapshot.queryParamMap.get('contractId'));
    if (Number.isFinite(targetContractId) && targetContractId > 0) {
      this.request.targetContractId = targetContractId;
    }
    this.route.queryParamMap.subscribe(params => {
      this.activeTab.set(this.resolveTab(params.get('tab')));
    });

    const currentCustomer = this.currentCustomer();
    const currentCompany = this.currentCompany();
    this.request.actorId = currentCustomer?.id;
    this.request.firstPartyId = currentCompany?.id;
    this.request.firstPartyRepId = currentCustomer?.id;

    this.loadPartyOptions();
    this.loadPreview();
  }

  setTab(tabId: string) {
    const resolvedTab = this.resolveTab(tabId);
    this.activeTab.set(resolvedTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: resolvedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  loadPreview() {
    if (!this.quotationId) return;
    this.loading.set(true);
    this.crmService.previewContractConversion(this.quotationId, this.request).subscribe({
      next: response => {
        this.loading.set(false);
        this.preview.set(response.data);
        this.items.set((response.data.items || []).map(item => ({ ...item, selected: item.selected !== false })));
        this.links.set(response.data.existingLinks || []);
        const quotation = response.data.quotation;
        this.request.effectiveStartDate = quotation?.startDate ? new Date(quotation.startDate) : this.request.effectiveStartDate;
        this.request.effectiveEndDate = quotation?.expiryDate ? new Date(quotation.expiryDate) : this.request.effectiveEndDate;
        if (quotation?.status && !this.isAllowedStatus(quotation.status)) {
          this.request.overrideReason = this.request.overrideReason || '';
        }
        this.previewLoaded = true;
        this.buyerDefaultResolved = false;
        this.maybeApplyPreviewBuyerDefault();
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Preview failed', detail: 'Could not load conversion preview.' });
      }
    });
  }

  convert() {
    if (this.converting()) {
      return;
    }
    if (!this.quotationId || !this.isRequestReady()) {
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'There are validation errors. Please review the highlighted fields.' });
      return;
    }
    this.converting.set(true);
    const payload: QuoteToContractRequest = {
      ...this.request,
      selectedItems: this.items().filter(item => item.selected)
    };
    this.crmService.convertQuotationToContract(this.quotationId, payload).subscribe({
      next: response => {
        this.converting.set(false);
        const contractId = response.data.contract?.contractID;
        this.messageService.add({ severity: 'success', summary: 'Draft created', detail: 'Quotation converted into a contract draft.' });
        if (contractId) {
          this.router.navigate(['/contracts/view', contractId]);
        }
      },
      error: error => {
        this.converting.set(false);
        this.messageService.add({ severity: 'error', summary: 'Conversion failed', detail: error?.error?.message || error?.error || 'Could not convert quotation.' });
      }
    });
  }

  onModeChange() {
    if (this.request.mode === 'AMENDMENT' && !this.request.targetContractId && this.links().length) {
      this.request.targetContractId = this.links()[0].contractId;
    }
    if (this.request.mode === 'AMENDMENT') {
      this.clearPartySelection('secondParty');
      this.clearPartySelection('firstParty');
    } else {
      this.initializeFirstPartyDefault();
      this.buyerDefaultResolved = false;
      this.maybeApplyPreviewBuyerDefault();
    }
    this.loadPreview();
  }

  onFirstPartyModeChanged(mode: PartyMode | null) {
    this.firstPartyMode = mode;
    if (mode === 'SELF') {
      this.selectedFirstPartyCrmOption = null;
      this.applySelfPartySelection('firstParty');
      return;
    }
    this.clearPartySelection('firstParty');
  }

  onSecondPartyModeChanged(mode: PartyMode | null) {
    this.secondPartyMode = mode;
    if (mode === 'SELF') {
      this.selectedSecondPartyCrmOption = null;
      this.applySelfPartySelection('secondParty');
      return;
    }
    this.clearPartySelection('secondParty');
  }

  onFirstPartyCrmOptionChanged(option?: ContractPartyOption | null) {
    this.selectedFirstPartyCrmOption = option || null;
    if (!option) {
      this.clearPartySelection('firstParty');
      return;
    }
    this.applyCrmPartySelection('firstParty', option);
  }

  onSecondPartyCrmOptionChanged(option?: ContractPartyOption | null) {
    this.selectedSecondPartyCrmOption = option || null;
    if (!option) {
      this.clearPartySelection('secondParty');
      return;
    }
    this.applyCrmPartySelection('secondParty', option);
  }

  targetItems(targetType: string) {
    return this.items().filter(item => item.targetType === targetType);
  }

  isAllowedStatus(status: string) {
    return [CrmQuotationStatus.APPROVED, CrmQuotationStatus.JOB, CrmQuotationStatus.COMPLETED].includes(status as CrmQuotationStatus);
  }

  isRequestReady() {
    const quote = this.preview()?.quotation;
    const allowed = !quote?.status || this.isAllowedStatus(quote.status) || !!this.request.overrideReason?.trim();
    const dates = !!this.request.effectiveStartDate;
    if (this.request.mode === 'AMENDMENT') {
      return allowed && dates && !!this.request.targetContractId;
    }
    return allowed
      && dates
      && !!this.request.firstPartyId
      && !!this.request.firstPartyRepId
      && !!this.request.secondPartyId
      && !!this.request.secondPartyRepId
      && !this.showBuyerLinkWarning();
  }

  warningSeverity(severity?: string) {
    return severity === 'ERROR' ? 'danger' : severity === 'WARN' ? 'warn' : 'info';
  }

  currentCompanyLabel() {
    return this.currentCompany()?.name || 'My company';
  }

  showBuyerLinkWarning() {
    return this.request.mode !== 'AMENDMENT' && this.quotationBuyerNeedsManualSelection && !this.request.secondPartyId;
  }

  private loadPartyOptions() {
    const countryId = this.sessionStorageService.getCountryList()?.find(country => country.countryCode === 'SA')?.id;
    const companyId = this.currentCompany()?.id;
    const companies$ = countryId ? this.companyService.getCompaniesByCountry(countryId) : of([] as Company[]);
    const clients$ = companyId ? this.crmService.getClients(companyId) : of([]);

    forkJoin({ companies: companies$, clients: clients$ }).subscribe({
      next: ({ companies, clients }) => {
        const options = buildContractPartyOptions(clients, companies || []);
        this.firstPartyCrmOptions = options.sellerOptions;
        this.secondPartyCrmOptions = options.buyerOptions;
        this.partyOptionsLoaded = true;
        this.initializeFirstPartyDefault();
        this.maybeApplyPreviewBuyerDefault();
      },
      error: () => {
        this.partyOptionsLoaded = true;
        this.initializeFirstPartyDefault();
        this.maybeApplyPreviewBuyerDefault();
      }
    });
  }

  private initializeFirstPartyDefault() {
    if (this.request.mode === 'AMENDMENT') {
      return;
    }
    this.firstPartyMode = 'SELF';
    this.selectedFirstPartyCrmOption = null;
    this.applySelfPartySelection('firstParty');
  }

  private maybeApplyPreviewBuyerDefault() {
    if (this.request.mode === 'AMENDMENT' || this.buyerDefaultResolved || !this.partyOptionsLoaded || !this.previewLoaded) {
      return;
    }

    if (this.request.secondPartyId || this.secondPartyMode === 'SELF' || this.selectedSecondPartyCrmOption) {
      this.buyerDefaultResolved = true;
      return;
    }

    const clientId = this.preview()?.quotation?.clientId;
    if (!clientId) {
      this.quotationBuyerNeedsManualSelection = false;
      this.buyerDefaultResolved = true;
      return;
    }

    const option = this.secondPartyCrmOptions.find(value => value.crmClientId === clientId) || null;
    if (!option) {
      this.secondPartyMode = 'CRM';
      this.selectedSecondPartyCrmOption = null;
      this.clearPartySelection('secondParty');
      this.quotationBuyerNeedsManualSelection = true;
      this.buyerDefaultResolved = true;
      return;
    }

    this.secondPartyMode = 'CRM';
    this.selectedSecondPartyCrmOption = option;
    this.applyCrmPartySelection('secondParty', option);
    this.quotationBuyerNeedsManualSelection = false;
    this.buyerDefaultResolved = true;
  }

  private applySelfPartySelection(party: 'firstParty' | 'secondParty') {
    const company = this.currentCompany();
    if (!company?.id) {
      this.clearPartySelection(party);
      return;
    }
    const customer = this.currentCustomer();
    this.applyResolvedCompany(party, company.id, customer?.id || undefined, undefined, customer || null);
  }

  private applyCrmPartySelection(party: 'firstParty' | 'secondParty', option: ContractPartyOption) {
    this.applyResolvedCompany(party, option.companyId, option.linkedCustomerId || undefined, option);
  }

  private applyResolvedCompany(
    party: 'firstParty' | 'secondParty',
    companyId: number,
    selectedRepId?: number,
    option?: ContractPartyOption,
    selectedRep?: Customer | null
  ) {
    if (party === 'firstParty') {
      this.request.firstPartyId = companyId;
      this.request.firstPartyRepId = selectedRepId || undefined;
      this.loadRepresentativesForParty('firstParty', companyId, selectedRepId, option, selectedRep);
      return;
    }
    this.request.secondPartyId = companyId;
    this.request.secondPartyRepId = selectedRepId || undefined;
    this.loadRepresentativesForParty('secondParty', companyId, selectedRepId, option, selectedRep);
  }

  private clearPartySelection(party: 'firstParty' | 'secondParty') {
    if (party === 'firstParty') {
      this.request.firstPartyId = undefined;
      this.request.firstPartyRepId = undefined;
      this.firstPartyReps = [];
      return;
    }
    this.request.secondPartyId = undefined;
    this.request.secondPartyRepId = undefined;
    this.secondPartyReps = [];
  }

  private loadRepresentativesForParty(
    party: 'firstParty' | 'secondParty',
    companyId: number,
    selectedRepId?: number,
    option?: ContractPartyOption,
    selectedRep?: Customer | null
  ) {
    this.customerService.getCustomersByCompany(companyId).subscribe(customers => {
      if (party === 'firstParty') {
        this.firstPartyReps = customers;
      } else {
        this.secondPartyReps = customers;
      }

      const matchedRep = selectedRep?.id
        ? customers.find(customer => customer.id === selectedRep.id) || null
        : selectedRepId
          ? customers.find(customer => customer.id === selectedRepId) || null
          : null;
      const preferredRep = matchedRep || resolvePreferredRepresentative(customers, option);
      if (party === 'firstParty') {
        this.request.firstPartyRepId = preferredRep?.id;
        return;
      }
      this.request.secondPartyRepId = preferredRep?.id;
    });
  }

  private currentCompany() {
    return this.sessionStorageService.getCompany() || this.toolbarService.customer()?.company || null;
  }

  private currentCustomer() {
    return this.toolbarService.customer() || this.sessionStorageService.getCustomer() || null;
  }

  private resolveTab(tab: string | null): string {
    return this.tabKeys.find(value => value === tab) || this.tabKeys[0];
  }
}
